import { Link, useParams } from "react-router-dom";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";
import { breadcrumbList, useSeo } from "../../lib/useSeo";
import { STATE_BY_SLUG, STATE_RULES_MARKETING } from "./data/stateRules";

export function StatePage() {
  const { slug } = useParams<{ slug: string }>();
  const state = slug ? STATE_BY_SLUG[slug] : undefined;

  useSeo(
    state
      ? {
          title: `Property operations software in ${state.name} — NestPlot`,
          description: `${state.name} property operations software with a state-compliant lease engine across multifamily, self-storage, parking, and RV/boat. Self-storage statute reference: ${state.statute}. Late-fee cap $${(state.lateFeeCapCents / 100).toFixed(0)} · ${state.gracePeriodDays}-day grace. 100% of customer payments land in your bank via your own Stripe.`,
          path: `/states/${state.slug}`,
          jsonLd: breadcrumbList([
            { name: "Home", path: "/" },
            { name: "States", path: "/states" },
            { name: state.name, path: `/states/${state.slug}` },
          ]),
        }
      : {
          title: "State not found — NestPlot",
          description: "We don't have a NestPlot page for that state. Browse all 50 states + DC.",
          path: "/states",
          robots: "noindex",
        },
  );

  if (!state) {
    return (
      <div className="mx-auto max-w-[760px] px-5 py-20 text-center">
        <h1 className="t-display-l">State not found</h1>
        <p className="text-sub mt-3">
          We don't have a page for that state. Browse{" "}
          <Link to="/states" className="text-accent font-semibold">
            all 50 states + DC
          </Link>
          .
        </p>
      </div>
    );
  }

  const lateFeeUsd = (state.lateFeeCapCents / 100).toFixed(0);
  const otherStates = STATE_RULES_MARKETING.filter((s) => s.code !== state.code).slice(0, 12);

  return (
    <div>
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
          <div>
            <div className="flex flex-wrap gap-2">
              <Pill tone="accent" size="sm">
                {state.name} · {state.code}
              </Pill>
              <Pill tone="ok" size="sm">
                State-compliant lease engine
              </Pill>
            </div>
            <h1 className="t-display-xl mt-4">
              Property operations software <br />
              <span className="text-accent">that knows {state.name} law.</span>
            </h1>
            <p className="text-lg text-sub mt-5 max-w-[560px]">
              NestPlot provides a {state.name}-compliant lease the moment you
              create a property — across multifamily, self-storage, parking,
              and RV/boat. Late-fee cap, grace period, notice windows, and
              recovery timing are all wired in per the statute that governs
              your vertical. Your customers e-sign in the rental flow; the
              signed PDF and audit trail land in their portal and your
              records. The summary card on the right shows the self-storage
              statute reference for {state.name}; the same per-state shape
              powers each of the other verticals.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/signup">
                <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
                  Start free · 30 days
                </Btn>
              </Link>
              <Link to="/pricing">
                <Btn kind="ghost" size="lg">
                  See pricing →
                </Btn>
              </Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <Card>
              <div className="bg-accent px-5 pt-3 pb-2">
                <Slats color="var(--on-accent)" opacity={0.55} height={10} />
              </div>
              <div className="px-5 py-5">
                <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.005em]">
                  {state.name} at a glance
                </div>
                <ul className="mt-3 divide-y divide-line text-sm">
                  <KV k="Statute" v={state.statute} />
                  <KV k="Late-fee cap" v={`$${lateFeeUsd}`} />
                  <KV k="Grace period" v={`${state.gracePeriodDays} days`} />
                  <KV k="Notice of default" v={`${state.noticeOfDefaultDays} days`} />
                  <KV k="Lien-sale notice" v={`${state.lienSaleNoticeDays} days`} />
                  <KV k="Move-out notice" v={`${state.moveOutNoticeDays} days`} />
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="bg-surface border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            How NestPlot handles {state.name} compliance
          </div>
          <h2 className="t-display-l mt-2">Built-in, not bolted on.</h2>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <Feature
              icon="grid"
              title="Lease template"
              body={`Provided per the statute that governs your vertical — landlord-tenant act, self-storage statute, parking lease law, or marina / RV agreement. The compliance check flags any clause that drifts outside the statute's bounds before you activate the version.`}
            />
            <Feature
              icon="wallet"
              title="Late-fee math"
              body={`${state.gracePeriodDays}-day grace window, then a fee capped at $${lateFeeUsd} per ${state.code} law for self-storage; equivalent caps apply for multifamily and parking per ${state.name} consumer-credit statute. NestPlot will never apply a late fee that exceeds the cap.`}
            />
            <Feature
              icon="alert"
              title="Reminder + recovery timing"
              body={`Notice-of-default lands at day ${state.noticeOfDefaultDays} for self-storage in ${state.name}; per-vertical recovery windows (court eviction for multifamily, lien sale for storage, impound for parking, vessel detention for marinas) are wired with the right state deadlines. The reminder ladder is operator-editable; defaults match each statute.`}
            />
            <Feature
              icon="key"
              title="Access fail-safe"
              body={`Reminders never auto-revoke a paying customer's access — gate code, fob, mobile key, LPR, or dock pass. You confirm before access is turned off — same in ${state.name} as everywhere else.`}
            />
            <Feature
              icon="user"
              title="Customer e-signature"
              body={`UETA / E-SIGN compliant out of the box. Customers sign with typed name, drawn signature, or uploaded image; the audit trail captures IP, timestamp, device, and a verified snapshot of exactly what they saw.`}
            />
            <Feature
              icon="download"
              title="Tax-ready exports"
              body={`Customer CSV, payment CSV, unit / stall / slip CSV, and a P&L PDF that breaks out sales tax (and parking surcharges where applicable) for ${state.name} remittance. Your data is yours; you can pull it any time, even on the trial.`}
            />
          </div>
        </div>
      </section>

      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 grid md:grid-cols-[1.4fr_1fr] gap-10">
          <div>
            <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
              The honest fine print for {state.name} operators
            </div>
            <h2 className="t-display-l mt-2">What lives with you, not us.</h2>
            <p className="text-sub mt-4">
              NestPlot handles your daily operator workflow — lease, billing,
              reminders, access, portal, reporting — across whichever
              vertical (or verticals) you run in {state.name}. A few
              compliance items stay with you, regardless of vertical:
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex gap-3">
                <Icon name="info" size={16} className="text-accent mt-[3px] shrink-0" />
                <span>
                  <strong>Insurance-licensed add-ons.</strong>{" "}
                  {["NY", "CA", "FL"].includes(state.code)
                    ? `${state.name} is one of the states that treats tenant-protection plans (storage) as insurance — a producer license is required to sell them. The same posture applies to renter's-insurance referrals (multifamily) and hull-insurance verification (marinas): NestPlot captures the customer's election but does not underwrite or act as a licensed producer.`
                    : `If you offer a tenant-protection plan (storage), a renter's-insurance referral (multifamily), or hull-insurance verification (marinas), and ${state.name}'s insurance code applies, the producer license is on you. NestPlot captures the customer's election but does not underwrite.`}
                </span>
              </li>
              <li className="flex gap-3">
                <Icon name="info" size={16} className="text-accent mt-[3px] shrink-0" />
                <span>
                  <strong>Sales tax + parking surcharges.</strong> We collect
                  the right amount per {state.name} rules, break it out on
                  every receipt, and itemize it on the P&amp;L PDF. You file
                  the return with your state.
                </span>
              </li>
              <li className="flex gap-3">
                <Icon name="info" size={16} className="text-accent mt-[3px] shrink-0" />
                <span>
                  <strong>The final recovery step.</strong> NestPlot drives
                  the notice windows ({state.lienSaleNoticeDays}-day lien
                  notice for self-storage in {state.name}; per-state windows
                  for court eviction, impound, and vessel detention in the
                  other verticals) and produces the required notice
                  documents. The final action — court filing in multifamily,
                  auction in self-storage (currently routed through
                  third-party platforms), tow in parking, vessel detention in
                  marinas — runs through you and your service providers.
                </span>
              </li>
            </ul>
          </div>
          <Card>
            <div className="px-5 py-5">
              <div className="font-display font-extrabold text-[16px] uppercase tracking-[-0.005em]">
                {state.name} statutory references
              </div>
              <div className="mt-3 space-y-3 text-sm">
                <div>
                  <div className="font-mono text-[10px] text-sub uppercase tracking-[0.1em]">
                    Self-storage statute
                  </div>
                  <div className="mt-1">{state.statute}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] text-sub uppercase tracking-[0.1em]">
                    Other verticals
                  </div>
                  <div className="mt-1 text-[12.5px] text-sub">
                    Multifamily falls under {state.name}'s landlord-tenant
                    act; parking under the state's parking lease /
                    impound code; marina and RV agreements under the
                    state's lien and contract law. NestPlot ships per-state
                    rules for each — your counsel can confirm the exact
                    citation for your operation.
                  </div>
                </div>
                <div className="text-sub text-xs border-t border-line pt-3">
                  NestPlot is not a law firm and these summaries are not legal
                  advice. Confirm the current statute with your counsel before
                  relying on any of these values for a specific customer.
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Other states NestPlot supports
          </div>
          <h2 className="t-display-l mt-2">All 50 states + DC.</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {otherStates.map((s) => (
              <Link
                key={s.code}
                to={`/states/${s.slug}`}
                className="border border-line rounded-md px-3 py-2 text-sm font-semibold hover:bg-paper hover:border-line-strong transition-colors"
              >
                {s.name}{" "}
                <span className="font-mono text-[11px] text-sub">{s.code}</span>
              </Link>
            ))}
          </div>
          <div className="mt-5">
            <Link to="/states" className="text-accent font-semibold">
              See all 50 states + DC →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <li className="py-2 flex justify-between gap-3">
      <span className="font-mono text-[10px] text-sub uppercase tracking-[0.1em] mt-[2px]">
        {k}
      </span>
      <span className="text-right font-semibold text-[13px]">{v}</span>
    </li>
  );
}

function Feature({
  icon,
  title,
  body,
}: {
  icon: "grid" | "wallet" | "alert" | "key" | "user" | "download";
  title: string;
  body: string;
}) {
  return (
    <Card>
      <div className="px-5 py-5">
        <div className="w-8 h-8 grid place-items-center rounded-md bg-accent-soft text-accent">
          <Icon name={icon as never} size={16} />
        </div>
        <div className="font-display font-extrabold text-[16px] uppercase tracking-[-0.005em] mt-3">
          {title}
        </div>
        <p className="text-sub text-sm mt-2">{body}</p>
      </div>
    </Card>
  );
}

export { StatePage as default };
